import { Text } from '@linktr.ee/arbor/components/Text';
import { CaretLeftIcon } from '@linktr.ee/arbor/icons';
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@shared/components/Button';
import { InputOTP } from '@shared/components/InputOTP';
import Layout from '@shared/components/Layout';
import {
  getAuthMfaChallengeRequestFailureReasonCode,
  getAuthMfaGenericInteractionScreenTitle,
  getAuthMfaVerificationFailureReasonCode,
  getAuthOtpChallengeRequestFailureReasonCode,
  getAuthOtpVerificationFailureReasonCode,
  publishAuthMfaChallengeRequestCompletedEvent,
  publishAuthMfaChallengeRequestedEvent,
  publishAuthMfaVerificationFailedEvent,
  publishAuthMfaVerificationSubmittedEvent,
  publishAuthOtpChallengeRequestCompletedEvent,
  publishAuthOtpChallengeRequestedEvent,
  publishAuthOtpVerificationFailedEvent,
  publishAuthOtpVerificationSubmittedEvent,
  publishButtonClickedEvent,
  type AuthOtpRequestTrigger,
} from '@shared/utils/analytics/events';
import { OTPVerificationScreenProps } from '@universal-login/types/descope';

const OTPVerificationScreen: React.FC<OTPVerificationScreenProps> = ({
  authFlow,
  otpChannel = 'email',
  mfaFlow,
  variant = 'default',
  bannerImageDesktop,
  hideLogo = true,
  headerClasses,
  consumeInitialOtpChallengeRequestTrigger,
  onFormUpdate,
  onOTPVerificationClick,
  onClickResend,
  onBackButtonClick,
  onClickUseDifferentNumber,
  onClickUseDifferentEmail,
  onClickChooseAnotherAuthMethod,
  onClickCancel,
  onLoginOtpRouteKnown,
  errorText,
  context,
  resetError,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [canResend, setCanResend] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);
  const hasReportedLoginOtpRouteKnown = useRef(false);
  const isSmsMfa = otpChannel === 'sms' && Boolean(mfaFlow);
  const mfaInteractionScreenTitle = mfaFlow
    ? getAuthMfaGenericInteractionScreenTitle(mfaFlow)
    : undefined;

  const publishChallengeRequested = useCallback(
    (requestTrigger: AuthOtpRequestTrigger) => {
      if (isSmsMfa && mfaFlow) {
        publishAuthMfaChallengeRequestedEvent(
          authFlow,
          requestTrigger,
          'sms',
          mfaFlow
        );
        return;
      }

      publishAuthOtpChallengeRequestedEvent(
        authFlow,
        requestTrigger,
        otpChannel
      );
    },
    [authFlow, isSmsMfa, mfaFlow, otpChannel]
  );

  const publishChallengeRequestCompleted = useCallback(
    (
      requestTrigger: AuthOtpRequestTrigger,
      requestOutcome: 'succeeded' | 'failed',
      error?: unknown
    ) => {
      if (isSmsMfa && mfaFlow) {
        publishAuthMfaChallengeRequestCompletedEvent(
          authFlow,
          requestTrigger,
          requestOutcome,
          'sms',
          mfaFlow,
          requestOutcome === 'failed'
            ? getAuthMfaChallengeRequestFailureReasonCode(error)
            : undefined
        );
        return;
      }

      publishAuthOtpChallengeRequestCompletedEvent(
        authFlow,
        requestTrigger,
        requestOutcome,
        requestOutcome === 'failed'
          ? getAuthOtpChallengeRequestFailureReasonCode(error)
          : undefined,
        otpChannel
      );
    },
    [authFlow, isSmsMfa, mfaFlow, otpChannel]
  );

  const publishVerificationSubmitted = useCallback(() => {
    if (isSmsMfa && mfaFlow) {
      publishAuthMfaVerificationSubmittedEvent(authFlow, 'sms', mfaFlow);
      return;
    }

    publishAuthOtpVerificationSubmittedEvent(authFlow, otpChannel);
  }, [authFlow, isSmsMfa, mfaFlow, otpChannel]);

  const publishVerificationFailed = useCallback(
    (error: unknown) => {
      if (isSmsMfa && mfaFlow) {
        publishAuthMfaVerificationFailedEvent(
          authFlow,
          getAuthMfaVerificationFailureReasonCode(error),
          'sms',
          mfaFlow
        );
        return;
      }

      publishAuthOtpVerificationFailedEvent(
        authFlow,
        getAuthOtpVerificationFailureReasonCode(error),
        otpChannel
      );
    },
    [authFlow, isSmsMfa, mfaFlow, otpChannel]
  );

  const publishMfaButtonClicked = useCallback(
    (componentName: string) => {
      if (!mfaInteractionScreenTitle) {
        return;
      }

      publishButtonClickedEvent(componentName, mfaInteractionScreenTitle);
    },
    [mfaInteractionScreenTitle]
  );

  useEffect(() => {
    if (!onLoginOtpRouteKnown || hasReportedLoginOtpRouteKnown.current) {
      return;
    }

    hasReportedLoginOtpRouteKnown.current = true;
    onLoginOtpRouteKnown();
  }, [onLoginOtpRouteKnown]);

  // Explicitly focus the OTP input when the screen mounts. React's `autoFocus`
  // alone is unreliable on mobile browsers after SPA transitions (the native
  // input may not receive focus, so the soft keyboard never opens). Calling
  // `.focus()` from an effect gives the browser a fresh, post-paint signal and
  // also protects against the input being (re)mounted while hidden.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      otpInputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const requestTrigger = consumeInitialOtpChallengeRequestTrigger?.();

    if (!requestTrigger) {
      return;
    }

    publishChallengeRequested(requestTrigger);
    publishChallengeRequestCompleted(requestTrigger, 'succeeded');
  }, [
    consumeInitialOtpChallengeRequestTrigger,
    publishChallengeRequestCompleted,
    publishChallengeRequested,
  ]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  // When the server returns an error for a submitted code, re-enable the form
  // and clear the input so the user can try again. On success the Descope
  // flow transitions to the next screen and this component unmounts, so we
  // intentionally keep the submitting state and the entered code visible
  // until then to avoid the form briefly appearing interactive while the
  // next screen is loading.
  useEffect(() => {
    if (errorText && isSubmitting) {
      publishVerificationFailed(errorText);
      setIsSubmitting(false);
      setCode('');
    }
  }, [errorText, isSubmitting, publishVerificationFailed]);

  const submitCode = async (value: string) => {
    // Clear any stale error from a previous failed submission before flipping
    // `isSubmitting`. Without this, the error-watching effect above would
    // immediately tear the loading state down for the new submission because
    // it sees the leftover `errorText`. This is the path users hit when they
    // paste a new code after an error: `InputOTP`'s paste handler calls
    // `onChange('')` then `onChange(fullCode)` via a 0ms timeout, so the
    // `value.length === 1` branch in `handleCodeChange` never fires and the
    // stale error is never cleared there.
    if (errorText) {
      resetError();
    }
    setIsSubmitting(true);
    try {
      publishVerificationSubmitted();
      onFormUpdate?.({ code: value });
      await onOTPVerificationClick(value);
    } catch (error) {
      publishVerificationFailed(error);
      // If the verification call throws (Descope SDK errors normally surface
      // via onError / onScreenUpdate instead, but be defensive), make sure
      // the form does not stay stuck in a submitting state.
      setIsSubmitting(false);
      setCode('');
    }
  };

  const handleCodeChange = async (value: string) => {
    if (isSubmitting) {
      return;
    }
    setCode(value);
    if (errorText && value.length === 1) {
      resetError();
    }
    if (value.length === 6) {
      await submitCode(value);
    }
  };

  const handleResend = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setCanResend(false);
    setCountdown(15);
    const requestTrigger = 'resend';

    try {
      publishMfaButtonClicked('Resend Code');
      publishChallengeRequested(requestTrigger);
      await onClickResend?.(); // Resend interaction
      publishChallengeRequestCompleted(requestTrigger, 'succeeded');
    } catch (error) {
      publishChallengeRequestCompleted(requestTrigger, 'failed', error);
      setCountdown(0);
      setCanResend(true);
    }
  };
  const formEmailOrUsername = context.form?.['form.emailOrUsername'];
  const formEmail = context.form?.email;
  const email =
    typeof formEmailOrUsername === 'string'
      ? formEmailOrUsername
      : typeof formEmail === 'string'
        ? formEmail
        : undefined;
  const isEmail = email?.includes('@') ?? false;
  const isSmsOtp = otpChannel === 'sms';
  const formPhone = context?.form?.phone;
  const phone =
    context?.sentTo?.maskedPhone ||
    (typeof formPhone === 'string' ? formPhone : undefined);
  const onClickUseDifferentContact =
    onClickUseDifferentEmail ?? onClickUseDifferentNumber;
  const useDifferentContactLabel = onClickUseDifferentEmail
    ? t('Use a different email')
    : t('Use a different number');
  const layoutProps =
    variant === 'banner'
      ? ({ variant, bannerImageDesktop, hideLogo, headerClasses } as const)
      : ({ variant, hideLogo, headerClasses } as const);

  return (
    <Layout
      heading={
        <>
          {onBackButtonClick && (
            <div className="relative w-20 mb-3 sm:left-28">
              <Button
                onClick={async () => {
                  await onBackButtonClick();
                }}
                variant="ghost"
                fullWidth={false}
                icon={<CaretLeftIcon />}
                label={t('Back')}
              />
            </div>
          )}
          <span className="text-center">
            {isSmsOtp ? t('Check your messages') : t('Check your inbox')}
          </span>
        </>
      }
      {...layoutProps}
      subHeading={
        isSmsOtp
          ? t('We sent a temporary 6-digit code to {{phone}}.', {
              phone,
            })
          : t('We sent a temporary 6-digit code to {{email}}.', {
              email: isEmail ? email : context?.sentTo?.maskedEmail,
            })
      }
    >
      <div className="space-y-6">
        <div className="flex justify-center">
          <InputOTP
            ref={otpInputRef}
            maxLength={6}
            value={code}
            onChange={(value: string | number) =>
              handleCodeChange(String(value))
            }
            onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
            disabled={isSubmitting}
            autoFocus
          >
            <InputOTP.Group>
              <InputOTP.Slot index={0} />
              <InputOTP.Slot index={1} />
              <InputOTP.Slot index={2} />
              <InputOTP.Slot index={3} />
              <InputOTP.Slot index={4} />
              <InputOTP.Slot index={5} />
            </InputOTP.Group>
          </InputOTP>
        </div>

        {errorText && (
          <Text
            as="p"
            variant="body-small-regular"
            className="text-red-600 text-center"
          >
            {errorText}
          </Text>
        )}

        <Button
          onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (code.length === 6 && !isSubmitting) {
              await submitCode(code);
            }
          }}
          // We deliberately don't pass `disabled` while submitting so the
          // button stays in its `loading` state. The component library's
          // `normaliseState` collapses any explicit `disabled` to `inactive`,
          // which would suppress the loading spinner. `state="loading"`
          // already makes the underlying <button> non-interactive (it sets
          // `inactive` and `pointer-events-none` internally).
          disabled={!isSubmitting && code.length !== 6}
          state={isSubmitting ? 'loading' : 'default'}
          label={t('Submit')}
        />

        <div className="text-center space-y-2">
          {canResend ? (
            <Text
              asChild
              variant="body-small-medium"
              className="text-black hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
            >
              <button
                type="button"
                onClick={handleResend}
                disabled={isSubmitting}
              >
                {t('Resend code')}
              </button>
            </Text>
          ) : (
            <Text as="p" variant="body-small-regular" className="text-gray-600">
              {t('Resend code in {{countdown}}s', { countdown })}
            </Text>
          )}
        </div>

        {(onClickUseDifferentContact ||
          onClickChooseAnotherAuthMethod ||
          onClickCancel) && (
          <div className="flex flex-col items-center gap-2 text-center">
            {onClickUseDifferentContact && (
              <Text
                asChild
                variant="body-small-medium"
                className="text-black hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
              >
                <button
                  type="button"
                  onClick={async () => {
                    publishMfaButtonClicked(
                      onClickUseDifferentEmail
                        ? 'Use Different Email'
                        : 'Use Different Number'
                    );
                    await onClickUseDifferentContact();
                  }}
                  disabled={isSubmitting}
                >
                  {useDifferentContactLabel}
                </button>
              </Text>
            )}
            {onClickChooseAnotherAuthMethod && (
              <Text
                asChild
                variant="body-small-medium"
                className="text-black hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
              >
                <button
                  type="button"
                  onClick={async () => {
                    publishMfaButtonClicked(
                      'Choose Another Authentication Method'
                    );
                    await onClickChooseAnotherAuthMethod();
                  }}
                  disabled={isSubmitting}
                >
                  {t('Choose another authentication method')}
                </button>
              </Text>
            )}
            {onClickCancel && (
              <Text
                asChild
                variant="body-small-medium"
                className="text-black hover:underline disabled:cursor-not-allowed disabled:opacity-50 disabled:no-underline"
              >
                <button
                  type="button"
                  onClick={async () => {
                    publishMfaButtonClicked('Cancel');
                    await onClickCancel();
                  }}
                  disabled={isSubmitting}
                >
                  {t('Cancel')}
                </button>
              </Text>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default OTPVerificationScreen;
